import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ChasePaymentRequest, FormType, Language } from './cmg-chase-payment-request';
import { TokenizedPaymentCard, SubmitOrderRequestPaymentMethodType } from '@/models';
import { getErrors, FormErrors } from './cmg-chase-errors';

@Component({
  name: 'CmgChaseForm',
})
export default class CmgChaseForm extends Vue {
  // #region Public Properties
  public isPaymentProviderInitializing = true;
  public errorMessages: string[] = [];

  @Prop({ required: true, type: Boolean })
  public oneTimePayment!: boolean;

  @Prop({ required: true, type: String })
  public providerId!: string;

  public frame!: HTMLFrameElement;
  // #endregion

  // #region Private Fields
  private sessionId!: string;
  // #endregion

  // #region Ctor
  constructor() {
    super();
  }
  // #endregion

  // #region Public Functions
  public created(): void {
    this.createWindowChaseCallbackFunctions();
  }

  public async mounted(): Promise<void> {
    try {
      this.$services.payment.loadChaseJsLibrary().then(async () => {
        const sessionToken = await this.getSessionToken();
        const iframe = this.$refs.frame as HTMLIFrameElement;
        if (iframe) {
          iframe.src = `${this.$services.config.getChaseBaseUrl()}?uID=${sessionToken}`;
        }
      });
    } catch (e: any) {
      this.errorMessages.push('Oops an error was encontered while fetching a session token from the service. Please try again!');
    }
  }

  public beforeDestroy() {
    (window as any).handlePaymentErrors = undefined;
    (window as any).completePayment = undefined;
    (window as any).startPayment = undefined;
    (window as any).hpfReady = undefined;
    (window as any).whatCVV2 = undefined;
  }

  get showErrors() {
    return this.errorMessages.length != 0;
  }

  // #endregion

  // #region Private Functions
  private clearErrorMessages(): void {
    this.errorMessages.length = 0;
  }

  private postErrors(errors: string[]): void {
    const clientSideErrors = getErrors(errors);

    if (clientSideErrors.includes('Unknown error')) {
      throw new Error('An error was encoutered trying to tokenize the card');
    }

    clientSideErrors.forEach(e => this.errorMessages.push(e));
  }

  private handlePaymentErrors(formErrors: FormErrors): void {
    const chaseErrors = formErrors.errorCode.filter(error => error);
    try {
      this.clearErrorMessages();
      this.postErrors(chaseErrors);
    } catch (err) {
      this.$services.logger.error(`An error was encoutered trying to tokenize the card: ${chaseErrors.toString()}`);
      this.closeModal();
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    } finally {
      this.isPaymentProviderInitializing = false;
    }
  }

  private async completePayment(): Promise<void> {
    this.isPaymentProviderInitializing = false;
    if (this.providerId) {
      try {
        const tokenizedCardResponse = await this.$services.transaction.getTransactionToken(this.providerId, this.sessionId);
        const tokenizedCard = {
          cardHolderName: tokenizedCardResponse.cardHolderName,
          expiration: tokenizedCardResponse.expiration,
          paymentMethod: tokenizedCardResponse.paymentMethod,
          tokenizedAccountNumber: tokenizedCardResponse.paymentToken,
          paymentProviderId: this.providerId,
          lastFourAccountNumbers: tokenizedCardResponse.last4,
        } as TokenizedPaymentCard;

        // if we landed here from one time payment modal, we want to just set the payment method
        // else, just saving the card
        if (this.oneTimePayment) {
          this.setOneTimePaymentMethod(tokenizedCard);
        } else {
          await this.savePaymentCard(tokenizedCard);
        }
      } catch (err: any) {
        this.$services.logger.error('An error was encountered while retrieving the tokenized card', err);
        this.closeModal();
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      }
    }
  }

  private startPayment(): void {
    this.isPaymentProviderInitializing = true;
  }

  private hpfReady(): void {
    this.isPaymentProviderInitializing = false;
  }

  private whatCVV2(): void {
    this.$services.ui.setModalVisibility('basicError', true, {
      header: 'CV..WHA?',
      content: 'You can find your 3-4 digit CVC on the back of most cards near your signature. AMEX is on the front.',
      isShort: true,
      options: [
        {
          text: 'OKAY',
          onClick: () => this.$services.ui.setModalVisibility('basicError', false),
        },
      ],
    });
  }

  private async savePaymentCard(tokenizedCard: any): Promise<void> {
    await this.$services.transaction
      .savePaymentCard(tokenizedCard)
      .then(() => {
        this.$services.transaction.refreshPaymentCards();
        this.closeModal();
      })
      .catch(() => {
        this.clearErrorMessages();
        this.errorMessages.push('Could not save credit card. Please try again later.');
      });
  }

  private setOneTimePaymentMethod(tokenizedCard: TokenizedPaymentCard) {
    this.$services.checkout.setPaymentMethods([
      {
        cardHolderName: tokenizedCard.cardHolderName!,
        creditCardSingleUseToken: tokenizedCard.tokenizedAccountNumber!,
        creditCardType: tokenizedCard.paymentMethod!.replace(/-/g, ' '),
        creditCardExpiration: tokenizedCard.expiration,
        paymentType: SubmitOrderRequestPaymentMethodType.CreditCard,
        paymentProviderId: this.providerId,
        lastFourAccountNumbers: tokenizedCard.lastFourAccountNumbers,
      },
    ]);
    if (tokenizedCard.expiration) {
      tokenizedCard.expirationMonth = tokenizedCard.expiration.substr(0, 2);
      tokenizedCard.expirationYear = tokenizedCard.expiration.substr(2, 2);
    }
    tokenizedCard.paymentMethod = tokenizedCard.paymentMethod.toLowerCase();
    tokenizedCard.isOneTimePaymentCard = true;
    this.$services.checkout.setOneTimePayCard(tokenizedCard);
    this.closeModal();
  }

  private createWindowChaseCallbackFunctions(): void {
    (window as any).handlePaymentErrors = this.handlePaymentErrors;
    (window as any).completePayment = this.completePayment;
    (window as any).startPayment = this.startPayment;
    (window as any).hpfReady = this.hpfReady;
    (window as any).whatCVV2 = this.whatCVV2;
  }

  private async getSessionToken(): Promise<string> {
    if (!this.providerId) {
      throw new Error('A valid provider is required');
    }

    try {
      const request: ChasePaymentRequest = new ChasePaymentRequest();
      request.formType = FormType.Iphone;
      request.lang = Language.English;
      const response = await this.$services.transaction.initializeRequest(this.providerId, request);
      if (!response.uId || !response.sessionId) {
        throw Error('No request data returned by the service');
      } else {
        this.sessionId = response.sessionId || '';
        return response.uId || '';
      }
    } catch (error: any) {
      this.$services.logger.error('An error was encountered while retrieving a session token from server.', error);
      throw new Error('The request session could not be fulfilled');
    }
  }

  private closeModal(): void {
    const args = this.$appStore.state.ui.modals.addCreditOrDebitCardModal.args;
    if (args && args.onClose) {
      args.onClose();
    }
    this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', false);
  }
  // #endregion
}
